import { useEffect, useState } from "react";
import type { ActionProposalArtifact, ActionProposalItem } from "~/shared/ai/assistant-action-artifact";

/** How many rows a card previews when its review holds more than the card shows. */
export const ACTION_PREVIEW_ROWS = 3;

/**
 * The first rows of a review too large to carry inline, read once through the same paged read the side pane uses. The
 * preview is display only: the card's selection and Save still cover every row.
 */
export function useActionPreviewItems(conversationId: string | null, artifact: ActionProposalArtifact | undefined): ActionProposalItem[] {
  const [items, setItems] = useState<ActionProposalItem[]>([]);
  const wanted = Boolean(artifact && !artifact.inlineItems && !artifact.groups && artifact.counts.candidates > 0);
  // One read per version and status of the review, so a replaced or saved review previews its own rows.
  const readKey = wanted && conversationId && artifact ? `${artifact.proposalId}|${artifact.version}|${artifact.status}` : null;
  useEffect(() => {
    const proposalId = readKey?.split("|")[0];
    if (!readKey || !conversationId || !proposalId) {
      setItems([]);
      return;
    }
    let active = true;
    const params = new URLSearchParams({ conversationId, proposalId });
    void fetch(`/api/rest/assistant-action-proposal-items?${params.toString()}`)
      .then(response => (response.ok ? (response.json() as Promise<{ items: ActionProposalItem[] }>) : { items: [] }))
      .then(page => {
        if (active) {
          setItems(page.items);
        }
      })
      .catch(() => {
        if (active) {
          setItems([]);
        }
      });
    return () => {
      active = false;
    };
  }, [conversationId, readKey]);
  return items;
}
